"use client"
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { useConfig } from './ConfigProvider';

// Define interface for AuthContext
interface AuthContextType {
    account: any | null | undefined; // Replace with appropriate type
    token: string | null | undefined;
    forceLogin: boolean;
    registrationError: string | null | undefined;
    login: () => Promise<void>;
    logout: () => void;
    register: () => void;
    authorize: (requiredRoles: string[]) => boolean;
    isAdmin: () => boolean;
    isInRole: (role: string) => boolean;
}

// Create AuthContext
const AuthContext = createContext<AuthContextType | undefined>(undefined);


export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { loginUrl } = useConfig();
    const [originalPath, setOriginalPath] = useState("");
    const [account, setAccount] = useState<any | null | undefined>(undefined); // State for the user account
    const [token, setToken] = useState<string | null | undefined>(undefined);
    const router = useRouter();
    const pathname = usePathname();
    const [registrationError, setRegistrationError] = useState<string | null>(null);
    const [forceLogin, setForceLogin] = useState<boolean>(false);
    const [instanceId, setInstanceId] = useState<string | null>(null);

    const getToken = useCallback(async () => {
        try {
            const response = await fetch(`${loginUrl}/api/auth/token`, {
                method: 'GET',
                credentials: "include"
            });

            if (!response.ok) {
                if (response.status === 401) {
                    const data = await response.json();
                    const errorMsg = data.error;
                    // Handle based on error message
                    if (errorMsg === "This user is already logged in from somewhere else") {
                        setRegistrationError("You are already logged in in another window. Click 'Force Log In' to log in from here and exit from the other sessions.");
                        setForceLogin(true);
                    }
                }
                setToken(null);
                setAccount(null);
                router.push("/login"); // Redirect to login page
                return null;
            }

            const data = await response.text();
            if (data) {
                return data;
            }
            else {
                console.error('Authentication failed');
                return null;
            }
        } catch (error) {
            console.error("Error:", error);
            setToken(null);
            setAccount(null);
            router.push("/login"); // Redirect to login page
            return null;
        }
    }, [loginUrl, router]);

    const decodeJwt = (token: string) => {
        const payload = token.split('.')[1]; // Get the payload part
        const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
        return JSON.parse(decoded);
    }

    const getInstanceId = (window: Window) => {
        // Get instance id after redirect after login
        const params = new URLSearchParams(window.location.search);

        const iid = params.get("instanceId");
        if (iid) {
            console.log("getInstanceId: callback instanceId: " + iid);
            window.name = iid; // Restore to window.name
        }

        if (window.name) { // Window tab persistence, if exists
            console.log("getInstanceId: persisted instanceId: " + window.name);
            return window.name;
        }

        const id = window.crypto?.randomUUID
            // Use crypto.randomUUID if available
            ? window.crypto.randomUUID()
            // Fallback for older browsers: simple UUID v4 generator
            : "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
                const r = (Math.random() * 16) | 0,
                    v = c === "x" ? r : (r & 0x3) | 0x8;
                return v.toString(16);
            });

        // Set windows tab persistence
        window.name = id;
        console.log("getInstanceId: new instanceId: " + window.name);
        return id;
    }

    const isAdmin = (): boolean => {
        if (account && account["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"]) {
            const userRoles = account["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"];
            if (userRoles) {
                return userRoles.includes("eForm_Administrator");
            } else {
                // we assume no roles being handled
                return true;
            }
        }
        return false;
    };

    const isInRole = useCallback((role: string): boolean => {
        if (account && account["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"]) {
            const userRoles = account["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"];
            if (userRoles) {
                return userRoles.includes(role);
            }
        }
        return false;
    }, [account]);

    useEffect(() => {
        const checkAuth = async () => {
            if (!account) {
                const token = await getToken();
                if (token?.includes("invalid_token")) {
                    setToken(null);
                    setAccount(null);
                    router.push("/login"); // Redirect to login page
                    return;
                }
                setToken(token);
                if (token) {
                    setAccount(decodeJwt(token));
                }
            }
        }

        if (account !== undefined) {
            if (account && (isInRole("eform_Denied") || !isInRole("eform_Allowed"))) {
                setRegistrationError("The registration hasn't been approved yet. Please contact the administrator.");
                //router.push("/error");
                return;
            }

            // Redirect to login page if not authenticated
            if (!account && pathname !== "/login") {
                setOriginalPath(pathname + window.location.search);
                router.push("/login"); // Redirect to login page
            } else if (account && pathname === "/login") {
                router.push(originalPath !== "" ? originalPath : "/reports"); // Redirect to reports page
                setForceLogin(false); // Reset forceLogin state
            }
        }
        else {
            // try to get current token
            if (loginUrl) {
                checkAuth();
            }
        }
    }, [account, loginUrl, pathname, router, originalPath, getToken, isInRole]);

    useEffect(() => {
        const id = getInstanceId(window);
        setInstanceId(id);
    }, []);

    const login = async () => {
        if (!instanceId) {
            console.warn("Instance ID not yet initialized");
            return;
        }

        try {
            var token = await getToken();
            setToken(token);
            if (token) {
                setAccount(decodeJwt(token)); // Set the authenticated account
            } else {
                // Add host:port (base URL) before originalPath
                const baseUrl = window.location.protocol + "//" + window.location.host;
                const returnUrl = encodeURIComponent(
                    originalPath
                        ? baseUrl + originalPath
                        : window.location.href
                );
                const forceLoginParam = forceLogin ? "&forceLogin=true" : "";
                window.location.href = `${loginUrl}/api/auth/login?returnUrl=${returnUrl}&instanceId=${instanceId}${forceLoginParam}`;
                setAccount(null);
            }
        } catch (error) {
            console.error('Login failed', error);
        }
    };

    const logout = () => {
        const returnUrl = encodeURIComponent(window.location.href);
        window.location.href = `${loginUrl}/api/auth/logout?returnUrl=${returnUrl}`
        setAccount(null); // Clear the account on logout
        setToken(null);
    };

    const register = () => {
        const returnUrl = encodeURIComponent(window.location.href);
        window.open(`${loginUrl}/Account/Register?ClientId=eform&returnUrl=${returnUrl}`, '_blank');
    };

    const authorize = (requiredRoles: string[]): boolean => {
        if (account && account["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"]) {
            const userRoles = account["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"];
            if (userRoles) {
                return requiredRoles.some(role => userRoles.includes(role));
            } else {
                // we assume no roles being handled
                return true;
            }
        }
        return false;
    };

    /* if (registrationError) {
        router.push("/login");
        //throw new Error(registrationError);
    } */

    return (
        <AuthContext.Provider value={{ account, token, registrationError, forceLogin, login, logout, register, authorize, isAdmin, isInRole }}>
            {children}
        </AuthContext.Provider>
    );
};

// Custom hook to use the Auth context
export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};